/**
 * puddle_layer — THE GROUND'S READ of the shallow-water puddle patch (shader gaps R6, 2026-09-30). The TSL half of
 * `lib/puddle_sim.ts`.
 *
 * NO SAMPLER. The ground is at its sampler limit (agent B measured ~16 sampled textures); the patch's look is a
 * STORAGE BUFFER (`ShallowWaterGpu.lookAttr`: one vec4 per cell — depth, the water surface's height relative to the
 * patch's `refY`, the surface slope, valid 0|1) read in the fragment stage with four `element()` loads: a storage
 * buffer counts against `maxStorageBuffersPerShaderStage` (8), never against the sampled textures. WebGPU only — the
 * ground is built with this term only when the sim decision is on (`puddleSimDecision`, which needs WebGPU); WebGL2
 * never sees a storage read (three would emulate it with a texture — a sampler).
 *
 * CRISP EDGES FROM A 0.5 m SIM. The patch holds the WATER LEVEL; the fragment compares it with ITS OWN ground — the
 * drawn height minus the same micro-relief the sim's bed lost (the static hollows' noise, evaluated per pixel) — so a
 * puddle's shore follows the hollow's contour at pixel resolution (Lagarde's "water level vs height" puddle rule),
 * never the 0.5 m cells. The level is interpolated over WET nodes only (a dry node's "level" is its ground, which is
 * not water), so no false film appears on dry ground between nodes.
 *
 * THE HAND-OVER. `weight` is 1 inside the patch, 0 at its edge (smoothstep over `HANDOVER_M`), × `uOn` (0 until the
 * patch's bed is built and its kernels are warm): the ground mixes its static fill → the sim's cover by it.
 */
import * as THREE from 'three/webgpu';
import { clamp, float, floor, int, max, min, select, smoothstep, storage, uniform } from 'three/tsl';
import { HANDOVER_M, PUDDLE_D0, PUDDLE_D1, PUDDLE_FILM_M, PUDDLE_SLOPE0, PUDDLE_SLOPE1, PUDDLE_WET_EPS } from './puddle_sim.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TSL node graphs are loosely typed (@types/three 0.182)
type N = any;

/** The one channel between the patch (the module writes it) and every ground material built to read it. */
export const puddleChannel = {
  /** Cells per side the buffer holds (0 = none yet). */
  n: 0,
  attr: null as THREE.StorageBufferAttribute | null,
  node: null as N,
  /** RENDER-space xz of node (0, 0) — the module writes it every frame (absolute − the render anchor, in doubles). */
  uMin: uniform(new THREE.Vector2(0, 0)),
  uCell: uniform(0.5),
  /** The absolute (= render) Y the look's levels are relative to. */
  uRefY: uniform(0),
  /** 1 once the patch is live (bed built, kernels warm), else the static fill everywhere. */
  uOn: uniform(0),
  /** Grounds built to read the channel (diagnostics). */
  grounds: 0,
};

/** The look buffer for an n×n patch — ONE object per n for the session (TSL binds the attribute at graph build). */
export function ensurePuddleChannel(n: number): THREE.StorageBufferAttribute {
  if (puddleChannel.attr && puddleChannel.n === n) return puddleChannel.attr;
  puddleChannel.n = n;
  puddleChannel.attr = new THREE.StorageBufferAttribute(new Float32Array(n * n * 4), 4);
  puddleChannel.node = storage(puddleChannel.attr, 'vec4', n * n).toReadOnly();
  return puddleChannel.attr;
}

export interface PuddleGroundTerm {
  /** The sim's puddle cover (the level mirror) at the fragment, 0..1. */
  cover: N;
  /** A film of flowing / shallow water over the fragment's ground — reads wet (darkening, gloss), 0..1. */
  wet: N;
  /** The hand-over weight: 1 inside the patch, 0 outside, × uOn. */
  weight: N;
  /** The water depth over the fragment's own ground (m; ≤ 0 where dry) — probes / debug. */
  depth: N;
}

/**
 * The puddle term at a RENDER-space position `wp` for ground whose micro-relief there is `reliefM` metres (the static
 * hollows × RELIEF_M + the ruts × RUT_M — the same numbers the sim's bed subtracted — plus the texture's crevices,
 * `lookTexReliefM`). Requires `ensurePuddleChannel`.
 */
export function puddleGroundTerm(wp: N, reliefM: N): PuddleGroundTerm {
  const ch = puddleChannel;
  const n = ch.n;
  if (!ch.node || n < 2) throw new Error('puddleGroundTerm: no channel (ensurePuddleChannel first)');
  ch.grounds++;
  const L: N = ch.node;
  const u: N = ch.uMin as N, c: N = ch.uCell as N;
  const lx: N = wp.x.sub(u.x).div(c), lz: N = wp.z.sub(u.y).div(c);
  const c0: N = clamp(floor(lx), 0, n - 2), r0: N = clamp(floor(lz), 0, n - 2);
  const fx: N = clamp(lx.sub(c0), 0, 1), fz: N = clamp(lz.sub(r0), 0, 1);
  const i00: N = int(r0).mul(n).add(int(c0));
  const a: N = L.element(i00), b: N = L.element(i00.add(1)), cc: N = L.element(i00.add(n)), d: N = L.element(i00.add(n + 1));
  const w00: N = float(1).sub(fx).mul(float(1).sub(fz)), w10: N = fx.mul(float(1).sub(fz));
  const w01: N = float(1).sub(fx).mul(fz), w11: N = fx.mul(fz);
  const wet = (s: N): N => select(s.x.greaterThan(PUDDLE_WET_EPS), float(1), float(0));
  const m00: N = w00.mul(wet(a)), m10: N = w10.mul(wet(b)), m01: N = w01.mul(wet(cc)), m11: N = w11.mul(wet(d));
  const ws: N = m00.add(m10).add(m01).add(m11);
  const eta: N = a.y.mul(m00).add(b.y.mul(m10)).add(cc.y.mul(m01)).add(d.y.mul(m11)).div(max(ws, float(1e-6))).add(ch.uRefY as N);
  const depth: N = eta.sub(wp.y.sub(reliefM));
  const slope: N = a.z.mul(w00).add(b.z.mul(w10)).add(cc.z.mul(w01)).add(d.z.mul(w11));
  const present: N = smoothstep(float(0), float(0.25), ws);
  // LINEAR in the water over this fragment's ground (lib/puddle_sim.ts PUDDLE_D0 / D1: a full hollow reads its depression)
  const cover: N = clamp(depth.sub(PUDDLE_D0).div(PUDDLE_D1 - PUDDLE_D0), 0, 1).mul(float(1).sub(smoothstep(float(PUDDLE_SLOPE0), float(PUDDLE_SLOPE1), slope))).mul(present);
  const wetF: N = smoothstep(float(0), float(PUDDLE_FILM_M), depth).mul(present);
  const edge: N = min(min(lx, float(n - 1).sub(lx)), min(lz, float(n - 1).sub(lz))).mul(c);
  // a cell the patch has not built yet (w 0) hands its fragments back to the static fill
  const valid: N = a.w.mul(w00).add(b.w.mul(w10)).add(cc.w.mul(w01)).add(d.w.mul(w11));
  const weight: N = smoothstep(float(0), float(HANDOVER_M), edge.sub(c)).mul(valid).mul(ch.uOn as N);
  return { cover, wet: wetF, weight, depth: select(ws.greaterThan(0), depth, float(-1)) };
}

/** A zero look (tests / a bed: the whole buffer dry). */
export function clearPuddleChannel(): void { if (puddleChannel.attr) { (puddleChannel.attr.array as Float32Array).fill(0); puddleChannel.attr.needsUpdate = true; } }
/** A vec4 for probes that read the channel's state. */
export const puddleChannelDebug = (): { n: number; grounds: number; on: number } => ({ n: puddleChannel.n, grounds: puddleChannel.grounds, on: (puddleChannel.uOn as unknown as { value: number }).value });
